import { c as _c } from "react/compiler-runtime";
import { IcoLink16, IcoLock16, IcoUsers16 } from '@onefootprint/icons';
import { Button, Popover, SegmentedControl, Tooltip, useToast } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChatSessionMutations } from '@/features/ask-percy';
import { buildChatShareUrl } from '../../utils/chat-share-url';
export type ChatShareControlProps = {
  chatSessionId: string;
  /** Current shared state from the session. */
  isShared: boolean;
  /** Whether the viewer created this session — only the owner may toggle sharing. */
  isOwner: boolean;
  /**
   * The session's env. Encoded into the copied link as `?is_live=` so a
   * same-tenant recipient (who can't see the session in their own lists) fetches
   * it against the right env instead of defaulting to live and 404ing a sandbox chat.
   */
  isLive?: boolean;
};
type ShareValue = 'private' | 'shared';

// Header icon-button chrome (matches the documents / layout / collapse buttons).
const triggerClass = 'flex items-center justify-center size-11 md:size-7 rounded-(--icon-button-border-radius) hover:bg-hover-control cursor-pointer focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-primary';
const ChatShareControl = t0 => {
  const $ = _c(71);
  const {
    chatSessionId,
    isShared,
    isOwner,
    isLive
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "share"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const {
    toast
  } = useToast();
  const {
    toggleShare
  } = useChatSessionMutations();
  const router = useRouter();
  const [shared, setShared] = useState(isShared);
  let t2;
  let t3;
  if ($[1] !== isShared) {
    t2 = () => {
      setShared(isShared);
    };
    t3 = [isShared];
    $[1] = isShared;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== chatSessionId || $[5] !== isLive || $[6] !== router.pathname || $[7] !== router.query || $[8] !== t || $[9] !== toast) {
    t4 = () => {
      const url = buildChatShareUrl({
        href: window.location.href,
        chatSessionId,
        isLive,
        pathname: router.pathname,
        query: router.query
      });
      navigator.clipboard.writeText(url);
      toast({
        title: t(_temp),
        description: t(_temp2)
      });
    };
    $[4] = chatSessionId;
    $[5] = isLive;
    $[6] = router.pathname;
    $[7] = router.query;
    $[8] = t;
    $[9] = toast;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const copyLink = t4;
  if (!isOwner) {
    if (!shared) {
      return null;
    }
    let t5;
    if ($[11] !== t) {
      t5 = t(_temp3);
      $[11] = t;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    let t6;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <span className="flex items-center justify-center w-7 h-7 rounded-full bg-secondary text-primary"><IcoUsers16 /></span>;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] !== t5) {
      t7 = <Tooltip content={t5} side="bottom">{t6}</Tooltip>;
      $[14] = t5;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    return t7;
  }
  let t5;
  if ($[16] !== chatSessionId || $[17] !== shared || $[18] !== toggleShare) {
    t5 = value => {
      const next = value === "shared";
      if (next === shared) {
        return;
      }
      setShared(next);
      toggleShare(chatSessionId, next);
    };
    $[16] = chatSessionId;
    $[17] = shared;
    $[18] = toggleShare;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const handleChange = t5;
  let t6;
  if ($[20] !== t) {
    t6 = t(_temp4);
    $[20] = t;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const t7 = shared ? "shared" : "private";
  let t8;
  if ($[22] !== t) {
    t8 = t(_temp5);
    $[22] = t;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t8) {
    t9 = {
      value: "private",
      label: t8,
      icon: IcoLock16
    };
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t) {
    t10 = t(_temp6);
    $[26] = t;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10) {
    t11 = {
      value: "shared",
      label: t10,
      icon: IcoUsers16
    };
    $[28] = t10;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11 || $[31] !== t9) {
    t12 = [t9, t11];
    $[30] = t11;
    $[31] = t9;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== handleChange || $[34] !== t12 || $[35] !== t6 || $[36] !== t7) {
    t13 = <SegmentedControl aria-label={t6} size="compact" value={t7} onChange={handleChange} options={t12} />;
    $[33] = handleChange;
    $[34] = t12;
    $[35] = t6;
    $[36] = t7;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  const toggle = t13;
  let t14;
  if ($[38] !== copyLink || $[39] !== shared || $[40] !== t) {
    t14 = shared && <Button variant="secondary" size="compact" prefixIcon={IcoLink16} onClick={copyLink}>{t(_temp7)}</Button>;
    $[38] = copyLink;
    $[39] = shared;
    $[40] = t;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  const copyButton = t14;
  let t15;
  if ($[42] !== t) {
    t15 = t(_temp8);
    $[42] = t;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== t15) {
    t16 = <p className="text-label-2 text-primary fp-text">{t15}</p>;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== shared || $[47] !== t) {
    t17 = shared ? t(_temp9) : t(_temp10);
    $[46] = shared;
    $[47] = t;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== t17) {
    t18 = <p className="text-body-3 text-tertiary fp-text">{t17}</p>;
    $[49] = t17;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== t18 || $[52] !== toggle) {
    t19 = <div className="flex flex-col gap-1.5">{toggle}{t18}</div>;
    $[51] = t18;
    $[52] = toggle;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== copyButton || $[55] !== t16 || $[56] !== t19) {
    t20 = <div className="flex flex-col gap-3 p-3 w-[260px]">{t16}{t19}{copyButton}</div>;
    $[54] = copyButton;
    $[55] = t16;
    $[56] = t19;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  let t21;
  if ($[58] !== t) {
    t21 = t(_temp11);
    $[58] = t;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  const t22 = shared && "text-accent";
  let t23;
  if ($[60] !== t22) {
    t23 = cx(triggerClass, t22);
    $[60] = t22;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] !== shared) {
    t24 = shared ? <IcoUsers16 /> : <IcoLock16 />;
    $[62] = shared;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  let t25;
  if ($[64] !== t21 || $[65] !== t23 || $[66] !== t24) {
    t25 = <button type="button" aria-label={t21} className={t23}>{t24}</button>;
    $[64] = t21;
    $[65] = t23;
    $[66] = t24;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] !== t20 || $[69] !== t25) {
    t26 = <Popover side="bottom" align="end" content={t20}>{t25}</Popover>;
    $[68] = t20;
    $[69] = t25;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  return t26;
};
export default ChatShareControl;
function _temp($) {
  return $.linkCopied;
}
function _temp2($_0) {
  return $_0.linkCopiedDescription;
}
function _temp3($_1) {
  return $_1.sharedTooltip;
}
function _temp4($_2) {
  return $_2.ariaLabel;
}
function _temp5($_3) {
  return $_3.private;
}
function _temp6($_4) {
  return $_4.shared;
}
function _temp7($_5) {
  return $_5.copyLink;
}
function _temp8($_6) {
  return $_6.title;
}
function _temp9($_7) {
  return $_7.sharedTooltip;
}
function _temp10($_8) {
  return $_8.privateDescription;
}
function _temp11($_9) {
  return $_9.title;
}